<template>
  <ValidationObserver>
    <UiModalContainer
      footer-classes="flex justify-end gap-3"
      :modal-show="modal"
      :modal-width="60"
      @handleClick="handleClick"
    >
      <template v-slot:header>
        <span>Generate Request</span>
      </template>
      <div>
        <InputFieldWrapper>
          <UiInput
            v-model="query"
            placeholder="SEARCH_STU"
            title="Search Student"
            type="search"
            :no-error="true"
            :hide-title="true"
            @change="getStudents"
          />
        </InputFieldWrapper>
        <Divider />
        <div v-if="isLoading">
          <Loader class="mt-20 mb-40" :content="true" />
        </div>
        <div v-else-if="!isLoading && isEmpty(students)">
          <NoRecordFound />
        </div>
        <TableWrapper v-else>
          <THead>
            <TRHead>
              <TH>Name</TH>
              <TH v-if="isAdmin">Pickup Person</TH>
              <TH v-for="(head, index) in tableHeaders" :key="index">{{ head }}</TH>
            </TRHead>
          </THead>
          <TBody>
            <TRBody v-for="(student, index) in students" :key="index">
              <TD>
                <SingleUserDisplay :user="student" label="full_name" :image="true" />
              </TD>
              <TD v-if="isAdmin">
                <UiSingleSelect
                  v-model="student.pickupPersonId"
                  child-class="mb-0 mt-5"
                  title="PICKUP_PERSON"
                  label="full_name"
                  :name="`Pickup Person ${index}`"
                  :options="student.student_info ? student.student_info.pickup_persons : []"
                  container-classes="w-[208px]"
                  class="flex-1 flex justify-center flex-col rounded-lg"
                  rules="required"
                  :hide-title="true"
                  reduce="user_id"
                />
              </TD>
              <TD>
                <BadgeDisplay
                  :options="student.users_roles_entities"
                  :is-object-concated="false"
                  :profile="true"
                  :nested-label="['class', 'title']"
                />
              </TD>
              <TD>
                <BadgeDisplay
                  :options="student.users_roles_entities"
                  :nested-label="['section', 'title']"
                  :is-object-concated="false"
                />
              </TD>
              <TD>
                <UIButton
                  button="primary"
                  :disabled="isRequestIsInProgress"
                  @click="generateRequest(student)"
                >
                  Request
                </UIButton>
              </TD>
            </TRBody>
          </TBody>
        </TableWrapper>
      </div>
      <template v-slot:footer></template>
    </UiModalContainer>
  </ValidationObserver>
</template>

<script>
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import { Form as ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters, mapState } from 'vuex'
import UiInput from '@components/UiElements/UiInputBox.vue'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import BadgeDisplay from '@src/components/UiElements/UiBadgeDisplay.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import isEmpty from 'lodash/isEmpty'
import generalUtil from '@src/mixins/general-mixins.js'
import SingleUserDisplay from '@src/components/UiElements/SingleUserDisplay.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import Divider from '@/src/components/Divider.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { objectDeepCopy } from '@src/utils/generalUtil.js'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'
import { TENANT_ROLES } from '@/src/constants/user-roles-constants'
export default {
  components: {
    UiModalContainer,
    ValidationObserver,
    UiInput,
    NoRecordFound,
    UiSingleSelect,
    BadgeDisplay,
    SingleUserDisplay,
    UIButton,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
    Divider,
    InputFieldWrapper,
    Loader,
  },
  mixins: [generalUtil],
  props: {
    modal: { type: Boolean },
    currentUser: { type: Object, default: () => {} },
  },
  emits: ['toggle'],
  data() {
    return {
      isEmpty,
      students: [],
      isLoading: false,
      query: '',
      isRequestIsInProgress: false,
      tableHeaders: ['Class', 'Section', 'Actions'],
    }
  },
  computed: {
    ...mapState('layout', ['currentCampusScope']),
    ...mapGetters('layout', ['userRole']),
    isAdmin() {
      return (
        this.userRole === TENANT_ROLES.SUPER_ADMIN || this.userRole === TENANT_ROLES.CAMPUS_ADMIN
      )
    },
  },
  created() {
    this.getStudents()
  },
  methods: {
    handleClick(eventName) {
      switch (eventName) {
        case 'close':
          this.$emit('toggle')
          break
        case 'generate':
          this.$emit('toggle', 'fetch')
      }
    },
    getStudents() {
      if (this.isAdmin) this.getSectionStudents(this.query)
      else this.getPikcupAssosiatedStudents(this.query)
    },
    getSectionStudents(query = '') {
      this.isLoading = true
      let payload = {
        $where: { ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', query) },
      }
      this.getStudentsWithFilters(query ? payload : '')
        .then((res) => {
          const records = res?.records || []
          // Create a deep copy so we don't accidentally mutate Vuex store state
          this.students = JSON.parse(JSON.stringify(records))
          if (this.students?.length > 0) {
            this.students.forEach((record) => {
              record?.student_info?.pickup_persons.forEach((pikcup) => {
                pikcup.full_name = `${pikcup.first_name} ${pikcup.last_name}`
              })
            })
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getPikcupAssosiatedStudents(query = '') {
      this.isLoading = true
      let payload = {
        $where: { ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', query) },
      }
      this.getAssociatedStudents(query ? payload : '').then((res) => {
        this.isLoading = false
        const records = res?.records || []
        this.students = objectDeepCopy(records)
        if (this.students?.length > 0) {
          this.students.forEach(
            (record) => (record.full_name = `${record.first_name} ${record.last_name}`),
          )
        }
      })
    },

    async generateRequest(student) {
      try {
        this.isRequestIsInProgress = true

        let payload = {
          student_id: student?.id,
          campus_id: this.currentCampusScope.id,
        }
        if (student.pickupPersonId) payload.pickup_person_id = student.pickupPersonId
        await this.generateNewPickupRequest(payload).then((response) => {
          this.$store.commit('layout/IS_LOADING', false)
          this.$store.commit('toast/NEW', {
            type: 'success',
            message: 'Request Generated',
          })
        })
        this.handleClick('generate')
      } catch (err) {
        this.isRequestIsInProgress = false
      }
    },
    ...mapActions('pickup', ['getAssociatedStudents', 'generateNewPickupRequest']),
    ...mapActions('student', ['getStudentsWithFilters']),
  },
}
</script>
